import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { 
  Plus, Fuel, TrendingUp, TrendingDown, AlertTriangle, Edit2, Truck, User, Calendar,
  DollarSign, Droplets, ArrowDownCircle, ArrowUpCircle, BarChart3, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const FuelModule = () => {
  const { user } = useContext(AuthContext);
  const [tanks, setTanks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loads, setLoads] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [operators, setOperators] = useState([]);
  const [stats, setStats] = useState(null);
  const [machineryStats, setMachineryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dialogOpen, setDialogOpen] = useState(null);

  const getLocalISODate = (date = new Date()) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getMonthStartISODate = () => {
    const date = new Date();
    date.setDate(1);
    return getLocalISODate(date);
  };

  const [fuelPdfFromDate, setFuelPdfFromDate] = useState(getMonthStartISODate());
  const [fuelPdfToDate, setFuelPdfToDate] = useState(getLocalISODate());
 // 'tank', 'purchase', 'load'
  
  // Form states
  const [tankForm, setTankForm] = useState({
    name: '', location: '', capacity: '', fuel_type: 'diesel', current_stock: 0, min_stock_alert: ''
  });
  const [editingTank, setEditingTank] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({
    tank_id: '', supplier_name: '', invoice_number: '', purchase_date: new Date().toISOString().split('T')[0],
    quantity: '', unit_price: '', notes: ''
  });
  const formatRegistrationDate = (date) => date ? new Date(date).toLocaleString("es-BO", { timeZone: "America/La_Paz", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [viewingPurchase, setViewingPurchase] = useState(null);
  const [loadForm, setLoadForm] = useState({
    tank_id: '', machinery_id: '', operator_id: '', load_date: new Date().toISOString().split('T')[0],
    quantity: '', horometer_reading: '', odometer_reading: '', notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditTank = (tank) => {
  setEditingTank(tank);

  setTankForm({
    name: tank.name,
    location: tank.location || "",
    capacity: tank.capacity,
    fuel_type: tank.fuel_type,
    current_stock: tank.current_stock,
    min_stock_alert: tank.min_stock_alert || ""
  });

  setDialogOpen("tank");
};

  const fetchData = async () => {
    try {
      const [tanksRes, purchasesRes, loadsRes, machineryRes, usersRes, statsRes, machStatsRes] = await Promise.all([
        axios.get(`${API}/fuel/tanks`),
        axios.get(`${API}/fuel/purchases`),
        axios.get(`${API}/fuel/loads`),
        axios.get(`${API}/machinery`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/fuel/stats/summary`),
        axios.get(`${API}/fuel/stats/by-machinery`)
      ]);
      setTanks(tanksRes.data);
      setPurchases(purchasesRes.data);
      setLoads(loadsRes.data);
      setMachinery(machineryRes.data);
      setOperators(usersRes.data);
      setStats(statsRes.data);
      setMachineryStats(machStatsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTank = async (e) => {
  e.preventDefault();

  const data = {
    ...tankForm,
    capacity: parseFloat(tankForm.capacity),
    current_stock: parseFloat(tankForm.current_stock) || 0,
    min_stock_alert: tankForm.min_stock_alert
      ? parseFloat(tankForm.min_stock_alert)
      : null
  };

  try {
    if (editingTank) {
      await axios.put(`${API}/fuel/tanks/${editingTank.id}`, data);
      toast.success("Tanque actualizado");
    } else {
      await axios.post(`${API}/fuel/tanks`, data);
      toast.success("Tanque creado");
    }

    setDialogOpen(null);
    setEditingTank(null);

    setTankForm({
      name: "",
      location: "",
      capacity: "",
      fuel_type: "diesel",
      current_stock: 0,
      min_stock_alert: ""
    });

    fetchData();
  } catch (error) {
    toast.error(error.response?.data?.detail || "Error al guardar tanque");
  }
};

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setPurchaseForm({
      tank_id: purchase.tank_id || "",
      supplier_name: purchase.supplier_name || "",
      invoice_number: purchase.invoice_number || "",
      purchase_date: purchase.purchase_date || new Date().toISOString().split("T")[0],
      quantity: purchase.quantity ?? "",
      unit_price: purchase.unit_price ?? "",
      notes: purchase.notes || ""
    });
    setDialogOpen("purchase");
  };

  const handleCreatePurchase = async (e) => {
    e.preventDefault();
    try {
      const purchaseData = {
        ...purchaseForm,
        quantity: parseFloat(purchaseForm.quantity),
        unit_price: parseFloat(purchaseForm.unit_price)
      };
      if (editingPurchase) {
        await axios.put(`${API}/fuel/purchases/${editingPurchase.id}`, purchaseData);
      } else {
        await axios.post(`${API}/fuel/purchases`, purchaseData);
      }
      toast.success('Compra registrada - Stock actualizado');
      setDialogOpen(null);
    setPurchaseForm({
      tank_id: "", supplier_name: "", invoice_number: "", purchase_date: new Date().toISOString().split("T")[0],
      quantity: "", unit_price: "", notes: ""
    });
    setEditingPurchase(null);
    fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar compra');
    }
  };

  const handleCreateLoad = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/fuel/loads`, {
        ...loadForm,
        quantity: parseFloat(loadForm.quantity),
        horometer_reading: loadForm.horometer_reading ? parseFloat(loadForm.horometer_reading) : null,
        odometer_reading: loadForm.odometer_reading ? parseFloat(loadForm.odometer_reading) : null
      });
      toast.success('Carga registrada - Stock actualizado');
      setDialogOpen(null);
      setLoadForm({
        tank_id: '', machinery_id: '', operator_id: '', load_date: new Date().toISOString().split('T')[0],
        quantity: '', horometer_reading: '', odometer_reading: '', notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar carga');
    }
  };

  const COLORS = ['#f97316', '#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6'];

  if (loading) {
    return <div className="text-center py-12">Cargando...</div>;
  }

  const dieselTanks = tanks.filter(tank => tank.fuel_type === "diesel");
  const gasolineTanks = tanks.filter(tank => tank.fuel_type === "gasolina");

  const dieselStock = dieselTanks.reduce((sum, tank) => sum + Number(tank.current_stock || 0), 0);
  const dieselCapacity = dieselTanks.reduce((sum, tank) => sum + Number(tank.capacity || 0), 0);
  const dieselPercent = dieselCapacity > 0 ? Math.min((dieselStock / dieselCapacity) * 100, 100) : 0;

  const gasolineStock = gasolineTanks.reduce((sum, tank) => sum + Number(tank.current_stock || 0), 0);
  const gasolineCapacity = gasolineTanks.reduce((sum, tank) => sum + Number(tank.capacity || 0), 0);
  const gasolinePercent = gasolineCapacity > 0 ? Math.min((gasolineStock / gasolineCapacity) * 100, 100) : 0;

  const dieselMachineryStats = machineryStats.filter(item => {
    const machine = machinery.find(m => String(m.id) === String(item.machinery_id));
    return machine?.fuel_type === "diesel";
  });

  const gasolineMachineryStats = machineryStats.filter(item => {
    const machine = machinery.find(m => String(m.id) === String(item.machinery_id));
    return machine?.fuel_type === "gasoline";
  });





  /* MAQCONTROL_FUEL_PDF_START */
  const exportFuelSummaryPDF = () => {
    try {
      if (!fuelPdfFromDate || !fuelPdfToDate) {
        toast.error("Seleccione la fecha inicial y final");
        return;
      }

      if (fuelPdfFromDate > fuelPdfToDate) {
        toast.error("La fecha inicial no puede ser mayor que la fecha final");
        return;
      }

      const doc = new jsPDF("p", "mm", "a4");

      const margin = 14;
      const pageWidth = 210;
      const pageHeight = 297;

      const orange = [234, 88, 12];
      const blue = [37, 99, 235];
      const green = [22, 163, 74];
      const dark = [31, 41, 55];
      const gray = [100, 116, 139];
      const light = [248, 250, 252];
      const border = [226, 232, 240];

      const number = (value) =>
        Number(value || 0).toLocaleString("es-BO", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

      const money = (value) =>
        `${Number(value || 0).toLocaleString("es-BO", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} Bs`;

      const fuelLabel = (type) => {
        if (type === "diesel") return "Diésel";
        if (type === "gasolina" || type === "gasoline") return "Gasolina";
        return type || "N/D";
      };

      const dateOnly = (value) => {
        if (!value) return "";
        return String(value).slice(0, 10);
      };

      const formatDate = (value) => {
        if (!value) return "-";
        const parts = String(value).slice(0, 10).split("-");
        if (parts.length !== 3) return value;
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      };

      const inPeriod = (value) => {
        const d = dateOnly(value);
        return d >= fuelPdfFromDate && d <= fuelPdfToDate;
      };

      const sum = (items, field) =>
        items.reduce(
          (total, item) => total + Number(item?.[field] || 0),
          0
        );

      const tankMap = {};
      tanks.forEach(t => {
        tankMap[String(t.id)] = t;
      });

      const machineryMap = {};
      machinery.forEach(m => {
        machineryMap[String(m.id)] = m;
      });

      const getTankFuelType = (tankId) => {
        return tankMap[String(tankId)]?.fuel_type || "";
      };

      const getMachineryFuelType = (machineryId) => {
        return machineryMap[String(machineryId)]?.fuel_type || "";
      };

      const getLoadFuelType = (load) => {
        return (
          getTankFuelType(load.tank_id) ||
          getMachineryFuelType(load.machinery_id) ||
          ""
        );
      };

      // ------------------------------------------------------
      // FILTRADO REAL POR PERÍODO
      // ------------------------------------------------------

      const periodPurchases = purchases.filter(p =>
        inPeriod(p.purchase_date)
      );

      const periodLoads = loads.filter(l =>
        inPeriod(l.load_date)
      );

      const dieselPurchases = periodPurchases.filter(
        p => getTankFuelType(p.tank_id) === "diesel"
      );

      const gasolinePurchases = periodPurchases.filter(
        p => getTankFuelType(p.tank_id) === "gasolina"
      );

      const dieselLoads = periodLoads.filter(
        l => getLoadFuelType(l) === "diesel"
      );

      const gasolineLoads = periodLoads.filter(
        l => getLoadFuelType(l) === "gasolina"
      );

      const dieselPurchaseQty = sum(dieselPurchases, "quantity");
      const gasolinePurchaseQty = sum(gasolinePurchases, "quantity");

      const dieselPurchaseCost = sum(dieselPurchases, "total_cost");
      const gasolinePurchaseCost = sum(gasolinePurchases, "total_cost");

      const dieselLoadQty = sum(dieselLoads, "quantity");
      const gasolineLoadQty = sum(gasolineLoads, "quantity");

      const dieselLoadCost = sum(dieselLoads, "total_cost");
      const gasolineLoadCost = sum(gasolineLoads, "total_cost");

      const dieselTanks = tanks.filter(
        t => t.fuel_type === "diesel"
      );

      const gasolineTanks = tanks.filter(
        t => t.fuel_type === "gasolina"
      );

      const totalCapacity = sum(tanks, "capacity");
      const totalStock = sum(tanks, "current_stock");

      const dieselCapacity = sum(dieselTanks, "capacity");
      const dieselStock = sum(dieselTanks, "current_stock");

      const gasolineCapacity = sum(gasolineTanks, "capacity");
      const gasolineStock = sum(gasolineTanks, "current_stock");

      const utilization = (stock, capacity) =>
        capacity > 0
          ? `${((stock / capacity) * 100).toFixed(1)}%`
          : "0.0%";

      // ------------------------------------------------------
      // RESUMEN POR MAQUINARIA DEL PERÍODO
      // ------------------------------------------------------

      const machineSummary = {};

      periodLoads.forEach(load => {
        const id = String(load.machinery_id || "sin-maquinaria");
        const machine = machineryMap[id];

        if (!machineSummary[id]) {
          machineSummary[id] = {
            code: machine?.code || load.machinery_code || "-",
            name:
              machine?.name ||
              load.machinery_name ||
              "Sin maquinaria",
            fuel_type:
              getLoadFuelType(load) ||
              machine?.fuel_type ||
              "",
            load_count: 0,
            quantity: 0,
            cost: 0,
            consumptionValues: []
          };
        }

        const item = machineSummary[id];

        item.load_count += 1;
        item.quantity += Number(load.quantity || 0);
        item.cost += Number(load.total_cost || 0);

        if (
          load.consumption_rate !== undefined &&
          load.consumption_rate !== null &&
          Number(load.consumption_rate) > 0
        ) {
          item.consumptionValues.push(
            Number(load.consumption_rate)
          );
        }
      });

      const machineRows = Object.values(machineSummary)
        .sort((a, b) => b.quantity - a.quantity)
        .map(item => {
          const avg =
            item.consumptionValues.length > 0
              ? item.consumptionValues.reduce((a, b) => a + b, 0) /
                item.consumptionValues.length
              : null;

          return [
            item.code,
            item.name,
            fuelLabel(item.fuel_type),
            String(item.load_count),
            `${number(item.quantity)} L`,
            money(item.cost),
            avg !== null ? `${number(avg)} L/h` : "-"
          ];
        });

      // ------------------------------------------------------
      // ENCABEZADO
      // ------------------------------------------------------

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(...dark);
      doc.text("MAQCONTROL", margin, 16);

      doc.setFontSize(12);
      doc.setTextColor(...orange);
      doc.text("RESUMEN DE COMBUSTIBLE", margin, 23);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...gray);

      doc.text(
        `Período: ${formatDate(fuelPdfFromDate)} al ${formatDate(fuelPdfToDate)}`,
        pageWidth - margin,
        15,
        { align: "right" }
      );

      doc.text(
        `Generado: ${new Date().toLocaleString("es-BO")}`,
        pageWidth - margin,
        21,
        { align: "right" }
      );

      doc.setDrawColor(...border);
      doc.line(margin, 27, pageWidth - margin, 27);

      // ------------------------------------------------------
      // RESUMEN GENERAL
      // ------------------------------------------------------

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...dark);
      doc.text("RESUMEN GENERAL", margin, 36);

      autoTable(doc, {
        startY: 40,
        theme: "grid",
        head: [["Indicador", "Resultado"]],
        body: [
          ["Período", `${formatDate(fuelPdfFromDate)} al ${formatDate(fuelPdfToDate)}`],
          ["Tanques", String(tanks.length)],
          ["Capacidad total", `${number(totalCapacity)} L`],
          ["Stock actual", `${number(totalStock)} L`],
          ["Utilización actual", utilization(totalStock, totalCapacity)],
          ["Compras en período", String(periodPurchases.length)],
          ["Cargas en período", String(periodLoads.length)],
          [
            "Litros comprados",
            `${number(dieselPurchaseQty + gasolinePurchaseQty)} L`
          ],
          [
            "Litros cargados",
            `${number(dieselLoadQty + gasolineLoadQty)} L`
          ]
        ],
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: dark,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        },
        columnStyles: {
          0: { cellWidth: 90, fontStyle: "bold" },
          1: { halign: "right" }
        }
      });

      let y = doc.lastAutoTable.finalY + 9;

      // ------------------------------------------------------
      // DISTRIBUCIÓN POR COMBUSTIBLE
      // ------------------------------------------------------

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...dark);
      doc.text("DISTRIBUCIÓN POR COMBUSTIBLE", margin, y);

      autoTable(doc, {
        startY: y + 4,
        theme: "grid",
        head: [[
          "Combustible",
          "Tanques",
          "Capacidad",
          "Stock",
          "Compras",
          "Cargas"
        ]],
        body: [
          [
            "Diésel",
            dieselTanks.length,
            `${number(dieselCapacity)} L`,
            `${number(dieselStock)} L`,
            `${number(dieselPurchaseQty)} L`,
            `${number(dieselLoadQty)} L`
          ],
          [
            "Gasolina",
            gasolineTanks.length,
            `${number(gasolineCapacity)} L`,
            `${number(gasolineStock)} L`,
            `${number(gasolinePurchaseQty)} L`,
            `${number(gasolineLoadQty)} L`
          ],
          [
            "TOTAL",
            tanks.length,
            `${number(totalCapacity)} L`,
            `${number(totalStock)} L`,
            `${number(dieselPurchaseQty + gasolinePurchaseQty)} L`,
            `${number(dieselLoadQty + gasolineLoadQty)} L`
          ]
        ],
        styles: {
          fontSize: 7.5,
          cellPadding: 2.5,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: dark,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        },
        didParseCell: function(data) {
          if (data.section === "body" && data.row.index === 0) {
            data.cell.styles.textColor = orange;
          }

          if (data.section === "body" && data.row.index === 1) {
            data.cell.styles.textColor = blue;
          }

          if (data.section === "body" && data.row.index === 2) {
            data.cell.styles.fontStyle = "bold";
          }
        }
      });

      y = doc.lastAutoTable.finalY + 9;

      // ------------------------------------------------------
      // COMPRAS
      // ------------------------------------------------------

      if (y > 250) {
        doc.addPage();
        y = 15;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...dark);
      doc.text(
        `COMPRAS DE COMBUSTIBLE (${formatDate(fuelPdfFromDate)} al ${formatDate(fuelPdfToDate)})`,
        margin,
        y
      );

      const purchaseRows = periodPurchases.map(p => [
        formatDate(p.purchase_date),
        p.tank_name ||
          tankMap[String(p.tank_id)]?.name ||
          "-",
        fuelLabel(getTankFuelType(p.tank_id)),
        `${number(p.quantity)} L`,
        `${number(p.unit_price)} Bs`,
        money(p.total_cost)
      ]);

      autoTable(doc, {
        startY: y + 4,
        theme: "grid",
        head: [[
          "Fecha",
          "Tanque",
          "Combustible",
          "Cantidad",
          "Precio/L",
          "Total"
        ]],
        body: purchaseRows.length
          ? purchaseRows
          : [["Sin compras en el período", "", "", "", "", ""]],
        styles: {
          fontSize: 7,
          cellPadding: 2,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: green,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        }
      });

      y = doc.lastAutoTable.finalY + 9;

      // ------------------------------------------------------
      // TOTALES DE COMPRAS
      // ------------------------------------------------------

      if (y > 250) {
        doc.addPage();
        y = 15;
      }

      autoTable(doc, {
        startY: y,
        theme: "grid",
        head: [["Combustible", "Cantidad", "Costo"] ],
        body: [
          [
            "Diésel",
            `${number(dieselPurchaseQty)} L`,
            money(dieselPurchaseCost)
          ],
          [
            "Gasolina",
            `${number(gasolinePurchaseQty)} L`,
            money(gasolinePurchaseCost)
          ],
          [
            "TOTAL",
            `${number(dieselPurchaseQty + gasolinePurchaseQty)} L`,
            money(dieselPurchaseCost + gasolinePurchaseCost)
          ]
        ],
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: dark,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        }
      });

      y = doc.lastAutoTable.finalY + 9;

      // ------------------------------------------------------
      // CARGAS
      // ------------------------------------------------------

      if (y > 245) {
        doc.addPage();
        y = 15;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...dark);
      doc.text(
        `CARGAS A MAQUINARIA (${formatDate(fuelPdfFromDate)} al ${formatDate(fuelPdfToDate)})`,
        margin,
        y
      );

      const loadRows = periodLoads.map(l => [
        formatDate(l.load_date),
        l.tank_name ||
          tankMap[String(l.tank_id)]?.name ||
          "-",
        fuelLabel(getLoadFuelType(l)),
        l.machinery_name ||
          machineryMap[String(l.machinery_id)]?.name ||
          "-",
        l.operator_name || "-",
        `${number(l.quantity)} L`,
        l.horometer_reading != null
          ? number(l.horometer_reading)
          : "-",
        l.odometer_reading != null
          ? number(l.odometer_reading)
          : "-"
      ]);

      autoTable(doc, {
        startY: y + 4,
        theme: "grid",
        head: [[
          "Fecha",
          "Tanque",
          "Comb.",
          "Máquina",
          "Operador",
          "Litros",
          "Horómetro",
          "Odómetro"
        ]],
        body: loadRows.length
          ? loadRows
          : [["Sin cargas en el período", "", "", "", "", "", "", ""]],
        styles: {
          fontSize: 6.2,
          cellPadding: 1.8,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: orange,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 24 },
          2: { cellWidth: 18 },
          3: { cellWidth: 28 },
          4: { cellWidth: 25 },
          5: { cellWidth: 16 },
          6: { cellWidth: 20 },
          7: { cellWidth: 20 }
        }
      });

      y = doc.lastAutoTable.finalY + 9;

      // ------------------------------------------------------
      // RESUMEN POR MAQUINARIA
      // ------------------------------------------------------

      if (y > 245) {
        doc.addPage();
        y = 15;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...dark);
      doc.text(
        `RESUMEN POR MAQUINARIA — PERÍODO SELECCIONADO`,
        margin,
        y
      );

      autoTable(doc, {
        startY: y + 4,
        theme: "grid",
        head: [[
          "Código",
          "Maquinaria",
          "Comb.",
          "Cargas",
          "Litros",
          "Costo",
          "Consumo prom."
        ]],
        body: machineRows.length
          ? machineRows
          : [["Sin cargas en el período", "", "", "", "", "", ""]],
        styles: {
          fontSize: 6.8,
          cellPadding: 2,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: blue,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        }
      });

      // ------------------------------------------------------
      // ESTADO ACTUAL DE TANQUES
      // ------------------------------------------------------

      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...dark);
      doc.text("ESTADO ACTUAL DE TANQUES", margin, 15);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...gray);
      doc.text(
        "Nota: el stock corresponde al estado actual del tanque, no a un saldo histórico del período.",
        margin,
        20
      );

      const tankRows = tanks.map(t => {
        const stock = Number(t.current_stock || 0);
        const capacity = Number(t.capacity || 0);
        const alert = Number(t.min_stock_alert || 0);
        const low = alert > 0 && stock <= alert;

        return [
          t.name || "-",
          fuelLabel(t.fuel_type),
          `${number(capacity)} L`,
          `${number(stock)} L`,
          utilization(stock, capacity),
          low ? "STOCK BAJO" : "NORMAL"
        ];
      });

      autoTable(doc, {
        startY: 25,
        theme: "grid",
        head: [[
          "Tanque",
          "Combustible",
          "Capacidad",
          "Stock",
          "Utilización",
          "Estado"
        ]],
        body: tankRows.length
          ? tankRows
          : [["Sin tanques", "", "", "", "", ""]],
        styles: {
          fontSize: 7.5,
          cellPadding: 2.5,
          textColor: dark,
          lineColor: border
        },
        headStyles: {
          fillColor: dark,
          textColor: [255, 255, 255],
          fontStyle: "bold"
        }
      });

      // ------------------------------------------------------
      // PIE DE PÁGINA
      // ------------------------------------------------------

      const pageCount = doc.internal.getNumberOfPages();

      for (let page = 1; page <= pageCount; page++) {
        doc.setPage(page);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...gray);

        doc.text(
          `MaqControl | Combustible | ${formatDate(fuelPdfFromDate)} al ${formatDate(fuelPdfToDate)}`,
          margin,
          pageHeight - 7
        );

        doc.text(
          `Página ${page} de ${pageCount}`,
          pageWidth - margin,
          pageHeight - 7,
          { align: "right" }
        );
      }

      const pdfBlob = doc.output("blob");
      const pdfURL = URL.createObjectURL(pdfBlob);

      window.open(pdfURL, "_blank");

      setTimeout(() => {
        URL.revokeObjectURL(pdfURL);
      }, 60000);

      toast.success(
        `PDF generado: ${formatDate(fuelPdfFromDate)} al ${formatDate(fuelPdfToDate)}`
      );

    } catch (error) {
      console.error("[FuelModule] Error generando PDF:", error);
      toast.error("No se pudo generar el PDF de combustible");
    }
  };
  /* MAQCONTROL_FUEL_PDF_END */

  return (
    <div data-testid="fuel-module-page">

        <div className="flex flex-wrap items-end justify-end gap-3 mb-4">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={fuelPdfFromDate}
              onChange={(e) => setFuelPdfFromDate(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={fuelPdfToDate}
              onChange={(e) => setFuelPdfToDate(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <button
            type="button"
            onClick={exportFuelSummaryPDF}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors shadow-sm text-sm font-medium"
          >
            📄 Exportar resumen PDF
          </button>
        </div>


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Control de Combustible
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">Gestión de tanques, compras y cargas interconectadas</p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
          <Button onClick={() => setDialogOpen('tank')} className="btn-secondary w-full sm:w-auto justify-center">
            <Plus size={18} className="mr-1" />
            Tanque
          </Button>
          <Button onClick={() => { setEditingPurchase(null); setDialogOpen('purchase'); }} className="bg-green-500 hover:bg-green-600 text-white w-full sm:w-auto justify-center">
            <ArrowDownCircle size={18} className="mr-1" />
            Compra
          </Button>
          <Button onClick={() => setDialogOpen('load')} className="btn-primary w-full sm:w-auto justify-center">
            <ArrowUpCircle size={18} className="mr-1" />
            Cargar Máquina
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 mb-6 border-b border-slate-200 overflow-x-auto scrollbar-hide">
        {[
          { id: 'overview', label: 'Resumen', icon: BarChart3 },
          { id: 'tanks', label: 'Tanques', icon: Fuel },
          { id: 'purchases', label: 'Compras (Entrada)', icon: ArrowDownCircle },
          { id: 'loads', label: 'Cargas (Salida)', icon: ArrowUpCircle }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center whitespace-nowrap px-3 sm:px-4 py-3 text-sm sm:text-base font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-600 hover:text-slate-800'
            }`}
          >
            <tab.icon size={18} className="mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white border border-orange-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <Fuel size={24} className="text-orange-500" />
                <span className="text-xs text-orange-600">Tanque Diésel</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {dieselStock.toLocaleString(undefined, { maximumFractionDigits: 2 })} L
              </p>
              <p className="text-sm text-slate-600">
                Capacidad: {dieselCapacity.toLocaleString(undefined, { maximumFractionDigits: 2 })} L
              </p>
              <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 rounded-full"
                  style={{ width: `${dieselPercent}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {dieselPercent.toFixed(1)}% disponible · {dieselTanks.length} tanque{dieselTanks.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="bg-white border border-blue-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <Fuel size={24} className="text-blue-500" />
                <span className="text-xs text-blue-600">Tanque Gasolina</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {gasolineStock.toLocaleString(undefined, { maximumFractionDigits: 2 })} L
              </p>
              <p className="text-sm text-slate-600">
                Capacidad: {gasolineCapacity.toLocaleString(undefined, { maximumFractionDigits: 2 })} L
              </p>
              <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${gasolinePercent}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {gasolinePercent.toFixed(1)}% disponible · {gasolineTanks.length} tanque{gasolineTanks.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <ArrowDownCircle size={24} className="text-green-600" />
                <span className="text-xs text-green-600">Compras del Mes</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{stats.monthly_purchases.quantity.toLocaleString()} L</p>
              <p className="text-sm text-green-600 mono">Bs. {stats.monthly_purchases.cost.toFixed(2)}</p>
              <p className="text-xs text-green-500">{stats.monthly_purchases.count} compras</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <ArrowUpCircle size={24} className="text-blue-600" />
                <span className="text-xs text-blue-600">Cargas del Mes</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">{stats.monthly_loads.quantity.toLocaleString()} L</p>
              <p className="text-sm text-blue-600 mono">Bs. {stats.monthly_loads.cost.toFixed(2)}</p>
              <p className="text-xs text-blue-500">{stats.monthly_loads.count} cargas</p>
            </div>

            {stats.alerts.low_stock_tanks > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <AlertTriangle size={24} className="text-red-600" />
                  <span className="text-xs text-red-600">Alertas</span>
                </div>
                <p className="text-2xl font-bold text-red-700">{stats.alerts.low_stock_tanks}</p>
                <p className="text-sm text-red-600">Tanques con stock bajo</p>
              </div>
            )}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Consumption by Machinery */}
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Consumo por Maquinaria</h3>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="border border-orange-200 rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-orange-700">Diésel</h4>
                      <p className="text-xs text-slate-500">Consumo por maquinaria</p>
                    </div>
                    <Fuel size={22} className="text-orange-500" />
                  </div>
                  {dieselMachineryStats.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={dieselMachineryStats.slice(0, 6)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="machinery_name" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${value} L`, "Consumo"]} />
                        <Bar dataKey="total_quantity" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Sin datos de consumo de diésel</p>
                  )}
                </div>
                <div className="border border-blue-200 rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-blue-700">Gasolina</h4>
                      <p className="text-xs text-slate-500">Consumo por maquinaria</p>
                    </div>
                    <Fuel size={22} className="text-blue-500" />
                  </div>
                  {gasolineMachineryStats.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={gasolineMachineryStats.slice(0, 6)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="machinery_name" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${value} L`, "Consumo"]} />
                        <Bar dataKey="total_quantity" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Sin datos de consumo de gasolina</p>
                  )}
                </div>
              </div>
            </div>

            {/* Top Consumers - Diesel / Gasoline */}
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                Top Consumidores
              </h3>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                {/* Diesel */}
                <div className="border border-orange-200 rounded-md p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <Fuel size={24} className="text-orange-500" />
                    <div>
                      <h4 className="font-semibold text-orange-700">Diésel</h4>
                      <p className="text-xs text-slate-500">Maquinarias con mayor consumo</p>
                    </div>
                  </div>

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2">Maquinaria</th>
                        <th className="text-right py-2">Litros</th>
                        <th className="text-right py-2">Costo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dieselMachineryStats.slice(0, 5).map((m, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 font-medium">{m.machinery_name}</td>
                          <td className="py-2 text-right mono">{m.total_quantity.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-orange-600">Bs. {m.total_cost.toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {dieselMachineryStats.length === 0 && (
                    <p className="text-center text-slate-500 py-6">
                      Sin datos de consumo de diésel
                    </p>
                  )}
                </div>

                {/* Gasoline */}
                <div className="border border-blue-200 rounded-md p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <Fuel size={24} className="text-blue-500" />
                    <div>
                      <h4 className="font-semibold text-blue-700">Gasolina</h4>
                      <p className="text-xs text-slate-500">Maquinarias con mayor consumo</p>
                    </div>
                  </div>

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2">Maquinaria</th>
                        <th className="text-right py-2">Litros</th>
                        <th className="text-right py-2">Costo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gasolineMachineryStats.slice(0, 5).map((m, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 font-medium">{m.machinery_name}</td>
                          <td className="py-2 text-right mono">{m.total_quantity.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-blue-600">Bs. {m.total_cost.toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {gasolineMachineryStats.length === 0 && (
                    <p className="text-center text-slate-500 py-6">
                      Sin datos de consumo de gasolina
                    </p>
                  )}
                </div>

              </div>
            </div>

          </div>
        </div>
      )}

      {/* Tanks Tab */}
      {activeTab === 'tanks' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tanks.map(tank => {
            const percentage = (tank.current_stock / tank.capacity) * 100;
            const isLow = tank.min_stock_alert && tank.current_stock <= tank.min_stock_alert;
            return (
              <div key={tank.id} className={`bg-white border rounded-md p-6 ${isLow ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
  <div className="flex items-center">
    <Fuel size={28} className={isLow ? "text-red-500" : "text-orange-500"} />
    <div className="ml-3">
      <h3 className="font-semibold text-slate-800">{tank.name}</h3>
      <p className="text-xs text-slate-500">
        {tank.location || "Sin ubicación"}
      </p>
    </div>
  </div>

  <div className="flex items-center gap-2">
    <button
      className="p-2 rounded hover:bg-slate-100 text-slate-600 hover:text-orange-600"
      onClick={() => handleEditTank(tank)}
      title="Editar tanque"
    >
      <Edit2 size={18} />
    </button>

    {isLow && (
      <AlertTriangle size={20} className="text-red-500" />
    )}
  </div>
</div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Stock Actual</span>
                    <span className="font-bold mono">{tank.current_stock.toLocaleString()} L</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${isLow ? 'bg-red-500' : 'bg-orange-500'}`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>0 L</span>
                    <span>{tank.capacity.toLocaleString()} L</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2">
                    <span className="text-slate-600">Tipo</span>
                    <span className="capitalize">{tank.fuel_type}</span>
                  </div>
                  {tank.min_stock_alert && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Alerta mínimo</span>
                      <span className="mono">{tank.min_stock_alert.toLocaleString()} L</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {tanks.length === 0 && (
            <div className="col-span-3 text-center py-12 text-slate-500">
              <Fuel size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No hay tanques registrados</p>
            </div>
          )}
        </div>
      )}

      {/* Purchases Tab */}
      {activeTab === 'purchases' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanque</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Proveedor</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Factura</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Cantidad</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Precio/L</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Total</th>
                  <th className="px-4 py-3 text-sm font-semibold text-slate-700">Registro</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map(p => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm">{new Date(p.purchase_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm font-medium">{p.tank_name}</td>
                    <td className="px-4 py-3 text-sm">{p.supplier_name}</td>
                    <td className="px-4 py-3 text-sm mono">{p.invoice_number || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right mono text-green-600">+{p.quantity.toLocaleString()} L</td>
                    <td className="px-4 py-3 text-sm text-right mono">Bs. {p.unit_price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right mono font-bold text-green-600">Bs. {p.total_cost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-center">{formatRegistrationDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setViewingPurchase(p)}>
                          <Eye size={15} className="mr-1" />Ver
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEditPurchase(p)}>
                          <Edit2 size={15} className="mr-1" />Editar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden p-3 space-y-3">
            {purchases.map(p => (
              <div key={p.id} className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{p.tank_name || 'Tanque'}</p>
                    <p className="text-xs text-slate-500">{new Date(p.purchase_date).toLocaleDateString()}</p>
                  </div>
                  <span className="shrink-0 px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs font-semibold">
                    Entrada
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Proveedor</p>
                    <p className="font-medium text-slate-700 break-words">{p.supplier_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Factura</p>
                    <p className="font-medium text-slate-700">{p.invoice_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Cantidad</p>
                    <p className="font-semibold text-green-600">{p.quantity.toLocaleString()} L</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Precio/L</p>
                    <p className="font-medium text-slate-700">Bs. {p.unit_price.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="font-bold text-green-700">Bs. {p.total_cost.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Registrado</p>
                    <p className="font-medium text-slate-700">{formatRegistrationDate(p.created_at)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewingPurchase(p)}
                    className="w-full"
                  >
                    <Eye size={15} className="mr-1" />Ver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditPurchase(p)}
                    className="w-full"
                  >
                    <Edit2 size={15} className="mr-1" />Editar
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {purchases.length === 0 && (
            <div className="text-center py-12 text-slate-500">No hay compras registradas</div>
          )}
        </div>
      )}

      {/* Loads Tab */}
      {activeTab === 'loads' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Maquinaria</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Operador</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanque</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Cantidad</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Horómetro</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">L/Hora</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Costo</th>
                </tr>
              </thead>
              <tbody>
                {loads.map(l => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm">{new Date(l.load_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm font-medium">{l.machinery_name}</td>
                    <td className="px-4 py-3 text-sm">{l.operator_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">{l.tank_name}</td>
                    <td className="px-4 py-3 text-sm text-right mono text-red-600">-{l.quantity.toLocaleString()} L</td>
                    <td className="px-4 py-3 text-sm text-right mono">{l.horometer_reading || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right mono">{l.consumption_rate || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right mono font-bold text-orange-600">Bs. {l.total_cost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden p-3 space-y-3">
            {loads.map(l => (
              <div key={l.id} className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">
                      {l.machinery_name || 'Maquinaria'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(l.load_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="shrink-0 px-2 py-1 rounded-md bg-orange-50 text-orange-700 text-xs font-semibold">
                    Salida
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Operador</p>
                    <p className="font-medium text-slate-700 break-words">
                      {l.operator_name || '-'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-slate-500">Tanque</p>
                    <p className="font-medium text-slate-700 break-words">
                      {l.tank_name || '-'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-slate-500">Cantidad</p>
                    <p className="font-semibold text-red-600">
                      -{l.quantity.toLocaleString()} L
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-slate-500">Costo</p>
                    <p className="font-bold text-orange-700">
                      Bs. {l.total_cost.toFixed(2)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-slate-500">Horómetro</p>
                    <p className="font-medium text-slate-700">
                      {l.horometer_reading || '-'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-slate-500">Consumo</p>
                    <p className="font-medium text-slate-700">
                      {l.consumption_rate || '-'} L/h
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {loads.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No hay cargas registradas
              </div>
            )}
          </div>

          <div className="hidden md:block">
            {loads.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No hay cargas registradas
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tank Dialog */}
      <Dialog open={dialogOpen === 'tank'} onOpenChange={() => setDialogOpen(null)}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>
  {editingTank ? "Editar Tanque" : "Nuevo Tanque de Combustible"}
</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTank} className="space-y-4 pb-1">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
              <input
                type="text"
                value={tankForm.name}
                onChange={(e) => setTankForm({ ...tankForm, name: e.target.value })}
                className="input-field"
                placeholder="Ej: Tanque Principal"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Capacidad (L) *</label>
                <input
                  type="number"
                  value={tankForm.capacity}
                  onChange={(e) => setTankForm({ ...tankForm, capacity: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo Combustible</label>
                <select
                  value={tankForm.fuel_type}
                  onChange={(e) => setTankForm({ ...tankForm, fuel_type: e.target.value })}
                  className="input-field"
                >
                  <option value="diesel">Diesel</option>
                  <option value="gasolina">Gasolina</option>
                  <option value="gas">Gas</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Stock Inicial (L)</label>
                <input
                  type="number"
                  value={tankForm.current_stock}
                  onChange={(e) => setTankForm({ ...tankForm, current_stock: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Alerta Mínimo (L)</label>
                <input
                  type="number"
                  value={tankForm.min_stock_alert}
                  onChange={(e) => setTankForm({ ...tankForm, min_stock_alert: e.target.value })}
                  className="input-field"
                  placeholder="Opcional"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Ubicación</label>
              <input
                type="text"
                value={tankForm.location}
                onChange={(e) => setTankForm({ ...tankForm, location: e.target.value })}
                className="input-field"
                placeholder="Ej: Patio principal"
              />
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" onClick={() => setDialogOpen(null)} className="btn-secondary w-full sm:w-auto justify-center">Cancelar</Button>
             <Button type="submit" className="btn-primary w-full sm:w-auto justify-center">
  {editingTank ? "Actualizar Tanque" : "Crear Tanque"}
</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Purchase Dialog */}
      <Dialog open={dialogOpen === 'purchase'} onOpenChange={() => setDialogOpen(null)}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
              <DialogTitle className="text-green-700">{editingPurchase ? "Editar Compra de Combustible (Entrada)" : "Registrar Compra de Combustible (Entrada)"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePurchase} className="space-y-4 pb-1">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tanque Destino *</label>
              <select
                value={purchaseForm.tank_id}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, tank_id: e.target.value })}
                className="input-field"
                required
              >
                <option value="">Seleccionar tanque...</option>
                {tanks.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.current_stock.toLocaleString()}/{t.capacity.toLocaleString()} L)</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Proveedor *</label>
                <input
                  type="text"
                  value={purchaseForm.supplier_name}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, supplier_name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nº Factura</label>
                <input
                  type="text"
                  value={purchaseForm.invoice_number}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, invoice_number: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha *</label>
                <input
                  type="date"
                  value={purchaseForm.purchase_date}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_date: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad (L) *</label>
                <input
                  type="number"
                  step="0.1"
                  value={purchaseForm.quantity}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, quantity: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Precio/Litro (Bs.) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={purchaseForm.unit_price}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, unit_price: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
            </div>
            {purchaseForm.quantity && purchaseForm.unit_price && (
              <div className="bg-green-50 p-3 rounded-md">
                <p className="text-sm text-green-700">Total: <span className="font-bold mono">Bs. {(parseFloat(purchaseForm.quantity) * parseFloat(purchaseForm.unit_price)).toFixed(2)}</span></p>
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
  type="button"
  onClick={() => {
    setDialogOpen(null);
    setEditingPurchase(null);
  }}
  className="btn-secondary w-full sm:w-auto justify-center"
>
  Cancelar
</Button>
                <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white w-full sm:w-auto justify-center">{editingPurchase ? "Actualizar Compra" : "Registrar Compra"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

        {/* Purchase Detail Dialog */}
        <Dialog open={!!viewingPurchase} onOpenChange={() => setViewingPurchase(null)}>
          <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl">
            <DialogHeader>
              <DialogTitle className="text-green-700 flex items-center gap-2"><Eye size={20} />Detalle de Compra de Combustible</DialogTitle>
            </DialogHeader>
            {viewingPurchase && (
              <div className="space-y-4 pb-1">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-sm text-green-700">Total de la compra</div>
                  <div className="text-2xl font-bold text-green-700">Bs. {Number(viewingPurchase.total_cost || 0).toFixed(2)}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><div className="text-xs text-slate-500">Fecha de compra</div><div className="font-medium">{viewingPurchase.purchase_date ? new Date(viewingPurchase.purchase_date).toLocaleDateString("es-BO") : "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Registrado el</div><div className="font-medium">{formatRegistrationDate(viewingPurchase.created_at)}</div></div>
                  <div><div className="text-xs text-slate-500">Tanque</div><div className="font-medium">{viewingPurchase.tank_name || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Proveedor</div><div className="font-medium">{viewingPurchase.supplier_name || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">N.º de factura</div><div className="font-medium">{viewingPurchase.invoice_number || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Cantidad</div><div className="font-medium">{Number(viewingPurchase.quantity || 0).toLocaleString()} L</div></div>
                  <div><div className="text-xs text-slate-500">Precio por litro</div><div className="font-medium">Bs. {Number(viewingPurchase.unit_price || 0).toFixed(2)}</div></div>
                  <div><div className="text-xs text-slate-500">Registrado por</div><div className="font-medium">{viewingPurchase.registered_by_name || "-"}</div></div>
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <div className="text-xs text-slate-500 mb-1">Observaciones</div>
                  <div className="bg-slate-50 rounded-md p-3 text-sm">{viewingPurchase.notes || "Sin observaciones"}</div>
                </div>
                <div className="flex justify-end">
                  <Button type="button" onClick={() => setViewingPurchase(null)} className="btn-secondary w-full sm:w-auto justify-center">Cerrar</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      {/* Load Dialog */}
      <Dialog open={dialogOpen === 'load'} onOpenChange={() => setDialogOpen(null)}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-orange-700">Cargar Combustible a Maquinaria (Salida)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLoad} className="space-y-4 pb-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tanque Origen *</label>
                <select
                  value={loadForm.tank_id}
                  onChange={(e) => setLoadForm({ ...loadForm, tank_id: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Seleccionar tanque...</option>
                  {tanks.map(t => (
                    <option key={t.id} value={t.id}>{t.name} (Disponible: {t.current_stock.toLocaleString()} L)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Maquinaria *</label>
                <select
                  value={loadForm.machinery_id}
                  onChange={(e) => setLoadForm({ ...loadForm, machinery_id: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Seleccionar maquinaria...</option>
                  {machinery.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Operador</label>
                <select
                  value={loadForm.operator_id}
                  onChange={(e) => setLoadForm({ ...loadForm, operator_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">Seleccionar...</option>
                  {operators.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha *</label>
                <input
                  type="date"
                  value={loadForm.load_date}
                  onChange={(e) => setLoadForm({ ...loadForm, load_date: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad (L) *</label>
                <input
                  type="number"
                  step="0.1"
                  value={loadForm.quantity}
                  onChange={(e) => setLoadForm({ ...loadForm, quantity: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Horómetro</label>
                <input
                  type="number"
                  step="0.1"
                  value={loadForm.horometer_reading}
                  onChange={(e) => setLoadForm({ ...loadForm, horometer_reading: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Odómetro</label>
                <input
                  type="number"
                  step="0.1"
                  value={loadForm.odometer_reading}
                  onChange={(e) => setLoadForm({ ...loadForm, odometer_reading: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" onClick={() => {
                setDialogOpen(null);
                setEditingTank(null);
              }} className="btn-secondary w-full sm:w-auto justify-center">Cancelar</Button>
              <Button type="submit" className="btn-primary w-full sm:w-auto justify-center">Registrar Carga</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelModule;
